import React, { useRef, useState } from 'react';
import { uploadFile } from '../../services/api';
import { Upload, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable image uploader.
 * Props:
 *  - category: 'tours' | 'categories' | 'blog'  (storage namespace)
 *  - multiple: boolean (default false)
 *  - onUpload(url: string): fired for EACH successful upload with the absolute URL
 *  - label: optional header text
 */
const ImageUploader = ({ category, multiple = false, onUpload, label = 'Görsel Yükle', className = '' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const pickFiles = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    setError('');
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress(0);
        const res = await uploadFile(category, f, (p) => setProgress(p));
        const absoluteUrl = `${BACKEND_URL}${res.data.url}`;
        onUpload?.(absoluteUrl);
        if (!multiple) break;
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (busy) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple={multiple}
        className="hidden"
        data-testid={`image-uploader-input-${category}`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <button
        type="button"
        onClick={pickFiles}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        disabled={busy}
        data-testid={`image-uploader-trigger-${category}`}
        className={`w-full flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
          busy
            ? 'border-[#C8D843] bg-[#C8D843]/10 text-[#3D4A26] cursor-wait'
            : 'border-gray-300 hover:border-[#3D4A26] hover:bg-gray-50 text-gray-600'
        } ${className}`}
      >
        {busy ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Yükleniyor… %{progress}
          </>
        ) : (
          <>
            <Upload size={16} />
            {label} {multiple && '(çoklu)'}
          </>
        )}
      </button>
      {error && (
        <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
